import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, MapPin, Loader2, Star, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/utils/analytics';

// Client-side email validation - mirrors backend blocklist exactly
const BLOCKED_DOMAINS = [
  // Example/test domains
  'example.com', 'example.org', 'example.net',
  'test.com', 'test.org', 'test.net',
  'fake.com', 'fake.org', 'fake.net',
  // "no" and similar fake domains
  'no.com', 'none.com', 'noemail.com', 'nowhere.com', 'nope.com',
  'null.com', 'void.com', 'nothing.com',
  // Disposable email services
  'mailinator.com', 'guerrillamail.com', 'tempmail.com',
  'throwaway.email', 'temp-mail.org', '10minutemail.com',
  'yopmail.com', 'sharklasers.com', 'trashmail.com',
  'disposable.com', 'fakeinbox.com', 'tempinbox.com',
  'maildrop.cc', 'mailnesia.com', 'mintemail.com',
  'mohmal.com', 'throwawaymail.com', 'getnada.com',
  'dispostable.com', 'mailcatch.com', 'emailondeck.com',
  'guerrillamailblock.com', 'spam4.me', 'grr.la',
];

const BLOCKED_LOCAL_PARTS = [
  'test', 'fake', 'spam', 'noreply', 'no-reply',
  'asdf', 'qwerty', 'admin', 'user', 'demo',
  'sample', 'example', 'null', 'undefined', 'temp',
  'no', 'none', 'nobody', 'nothing', 'void',
  'info', 'hello', 'contact', 'support',
];

const BLOCKED_PATTERNS = [
  /^test\d*$/i,      // test, test1, test123
  /^fake\d*$/i,      // fake, fake1
  /^temp\d*$/i,      // temp, temp1
  /^user\d+$/i,      // user1, user123
  /^a{3,}$/i,        // aaa, aaaa
  /^asdf/i,          // asdf, asdfg
  /^qwerty/i,        // qwerty
  /^no\d*$/i,        // no, no1, no123
  /^none\d*$/i,      // none, none1
  /^x+$/i,           // x, xx, xxx
  /^null\d*$/i,      // null, null1
];

function validateEmail(email: string): { valid: boolean; error?: string } {
  const trimmed = email.trim().toLowerCase();
  
  if (!trimmed) {
    return { valid: false, error: 'Please enter your email address' };
  }

  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  const [localPart, domain] = trimmed.split('@');

  // Check blocked domains
  if (BLOCKED_DOMAINS.some(d => domain === d || domain.endsWith('.' + d))) {
    return { valid: false, error: 'Please use a real email address, not a test or temporary email' };
  }

  // Check blocked local parts
  if (BLOCKED_LOCAL_PARTS.includes(localPart)) {
    return { valid: false, error: 'Please use your real email address' };
  }

  // Check blocked patterns
  if (BLOCKED_PATTERNS.some(pattern => pattern.test(localPart))) {
    return { valid: false, error: 'Please use a real email address' };
  }

  return { valid: true };
}

// Extract error message from Supabase invoke response
function extractErrorMessage(error: { message?: string } | null, responseData: { error?: string } | null): string {
  // First check if we got a validation error in the response data
  if (responseData?.error && typeof responseData.error === 'string') {
    return responseData.error;
  }

  // Check for error message
  if (!error?.message) {
    return 'Failed to create calendar. Please try again.';
  }

  const message = error.message;

  // Try to extract JSON error from the message
  // Format is typically: "Edge function returned 400: Error, {"error":"message"}"
  const jsonMatch = message.match(/\{[^}]+\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      if (parsed?.error) {
        return parsed.error;
      }
    } catch {
      // JSON parse failed, continue to fallbacks
    }
  }

  // Check for common validation phrases
  if (message.includes('real email') || message.includes('valid email')) {
    return 'Please use a real email address';
  }

  // If it's just the generic Supabase error, return friendly message
  if (message.includes('Edge function returned') || message.includes('non-2xx')) {
    return 'Unable to create calendar. Please check your email and try again.';
  }

  return message;
}

interface CreateAnnualCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: {
    name: string;
    latitude: number;
    longitude: number;
  };
  selectedYear: number;
}

export const CreateAnnualCalendarModal: React.FC<CreateAnnualCalendarModalProps> = ({
  isOpen,
  onClose,
  location,
  selectedYear,
}) => {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [year, setYear] = useState(selectedYear.toString());
  const [isLoading, setIsLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  // Client-side email validation
  const emailValidation = useMemo(() => {
    if (user) return { valid: true }; // Logged-in users use their verified email
    if (!email.trim()) return { valid: false, error: undefined }; // Don't show error for empty
    return validateEmail(email);
  }, [email, user]);

  const canSubmit = user || (email.trim() && emailValidation.valid);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Double-check validation before submitting
    if (!user && !emailValidation.valid) {
      toast.error(emailValidation.error || 'Please enter a valid email');
      return;
    }

    setIsLoading(true);

    try {
      const emailToUse = user?.email || email.trim();
      
      if (!emailToUse) {
        toast.error('Please enter your email address');
        setIsLoading(false);
        return;
      }

      const response = await supabase.functions.invoke('create-shared-calendar', {
        body: {
          email: emailToUse,
          location_name: location.name,
          latitude: location.latitude,
          longitude: location.longitude,
          year: parseInt(year),
          user_id: user?.id || null,
          is_premium: isSubscribed,
          subscribe_newsletter: !isSubscribed,
        },
      });

      // Check for validation errors in response data first
      if (response.data?.error) {
        toast.error(response.data.error);
        setIsLoading(false);
        return;
      }

      if (response.error) {
        const errorMessage = extractErrorMessage(response.error, response.data);
        toast.error(errorMessage);
        setIsLoading(false);
        return;
      }

      const data = response.data;

      if (data?.slug) {
        trackEvent('annual_calendar_created', {
          location: location.name,
          year: parseInt(year),
          user_type: isSubscribed ? 'premium' : user ? 'registered' : 'guest',
        });
        
        toast.success('Your annual calendar has been created!');
        onClose();
        navigate(`/calendar/${data.slug}`);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error creating calendar:', error);
      const errorMessage = extractErrorMessage(error, null);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // For premium users, skip the modal entirely
  if (isSubscribed && user?.email) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calendar className="w-5 h-5 text-gold" />
            Create Annual Calendar
          </DialogTitle>
          <DialogDescription>
            Get the best Milky Way photography nights for the entire year
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          {/* Location display */}
          <div className="p-3 rounded-lg bg-secondary/30 border border-border/50">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">{location.name}</p>
                <p className="text-sm text-muted-foreground">
                  {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
                </p>
              </div>
            </div>
          </div>

          {/* Year selector */}
          <div className="space-y-2">
            <Label htmlFor="year">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger>
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Email input - only for non-logged-in users */}
          {!user && (
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  // Show validation error immediately if email contains @
                  if (newEmail.includes('@')) {
                    const validation = validateEmail(newEmail);
                    if (!validation.valid) {
                      setInlineError(validation.error || 'Please use a real email address');
                    } else {
                      setInlineError(null);
                    }
                  } else {
                    setInlineError(null);
                  }
                }}
                required
                className={(inlineError || emailValidation.error) ? 'border-destructive focus-visible:ring-destructive' : ''}
              />
              {(inlineError || emailValidation.error) && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {inlineError || emailValidation.error}
                </p>
              )}
            </div>
          )}

          {/* Show email for logged-in free users */}
          {user && !isSubscribed && (
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="p-2 rounded bg-secondary/20 text-sm text-muted-foreground">
                {user.email}
              </div>
            </div>
          )}

          {/* Privacy note - only for non-premium */}
          {!isSubscribed && !emailValidation.error && (
            <p className="text-xs text-muted-foreground">
              We'll send you occasional photography tips and updates. No spam, unsubscribe anytime.
            </p>
          )}

          {/* What you'll get */}
          <div className="p-3 rounded-lg bg-gold/5 border border-gold/20">
            <p className="text-sm font-medium text-gold mb-2 flex items-center gap-2">
              <Star className="w-4 h-4" />
              What you'll get:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-gold">✦</span>
                Full {year} calendar for {location.name}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gold">✦</span>
                Shareable calendar page
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gold">✦</span>
                Downloadable PDF calendar
              </li>
              <li className="flex items-center gap-2">
                <span className="text-gold">✦</span>
                Downloadable ICS to import into your calendar
              </li>
            </ul>
          </div>

          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading || !canSubmit}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Create My Calendar
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
